import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {TextInputField} from '@/components/molecules';
import {TextArea, TargetTypeLabel} from '@/components/atoms';
import {
  STRING_SCHEMA,
  DISPLAY_CONTENT_FIELD_CONFIG as FIELD_CONFIG
} from '@/services/schema/displayContentSchema';
import {SALES_TARGET_MAIN_GROUP} from '@/utils/constants/globalConstants';
import {
  TARGET_TYPES,
  DISPLAY_CONTENT_VARIANT
} from '@/utils/constants/saleTargetConstants';

// --- Variant field configurations ---
const VARIANT_CONFIGS = {
  [DISPLAY_CONTENT_VARIANT.MAIN]: {
    inputRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2',
        fields: [
          {key: 'title', configKey: 'title', required: true},
          {key: 'category', configKey: 'category', required: true}
        ]
      }
    ],
    textareaRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 md:gap-y-6',
        fields: [
          {key: 'excerpt', configKey: 'excerpt'},
          {key: 'terms', configKey: 'terms'}
        ]
      }
    ]
  },
  [DISPLAY_CONTENT_VARIANT.LAYERED]: {
    inputRows: [
      {
        className: 'mb-4 grid grid-cols-4 gap-x-10',
        fields: [
          {key: 'title', configKey: 'title', required: true},
          {key: 'pointsEarned', configKey: 'pointsEarned', required: true},
          {key: 'parentCategory', configKey: 'parentCategory'},
          {key: 'childCategory', configKey: 'childCategory'}
        ]
      }
    ],
    textareaRows: [
      {
        className: 'display-content-textarea gap-x-10',
        fields: [
          {key: 'excerpt', configKey: 'excerptLayered'},
          {key: 'terms', configKey: 'termsLayered'}
        ]
      }
    ]
  },
  [DISPLAY_CONTENT_VARIANT.EARLY_PAYMENT]: {
    inputRows: [
      {
        className: 'mb-6 grid grid-cols-1 gap-x-10',
        dataTestId: 'title-row',
        fields: [{key: 'title', configKey: 'titleEarlyPayment', required: true}]
      }
    ],
    textareaRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 md:gap-y-6',
        dataTestId: 'textarea-wrapper',
        fields: [
          {key: 'description', configKey: 'description'},
          {key: 'terms', configKey: 'termsEarlyPayment'}
        ]
      }
    ]
  }
};

const DisplayContentTab = (props) => {
  const {
    setDisplayContentFormData,
    displayContentFormData,
    activeLanguage,
    languages,
    targetType
  } = props;

  const data = useMemo(
    () => displayContentFormData[activeLanguage] || {},
    [displayContentFormData, activeLanguage]
  );

  const handleFieldChange = useCallback(
    (fieldName, value, hasError) => {
      setDisplayContentFormData((prevData) => ({
        ...prevData,
        [activeLanguage]: {
          ...prevData[activeLanguage],
          [fieldName]: value,
          [`${fieldName}Valid`]: hasError
        }
      }));
    },
    [setDisplayContentFormData, activeLanguage]
  );

  const renderInputField = useCallback(
    (field) => (
      <TextInputField
        key={`${field.key}_${activeLanguage}`}
        value={data?.[field.key] || ''}
        label={FIELD_CONFIG[field.configKey].label}
        updateValue={(val, hasError) =>
          handleFieldChange(field.key, val, hasError)
        }
        schema={STRING_SCHEMA}
        uiSchema={FIELD_CONFIG[field.configKey]}
        required={field.required}
        visible
      />
    ),
    [data, handleFieldChange, activeLanguage]
  );

  const renderTextArea = useCallback(
    (field) => (
      <TextArea
        key={`${field.key}_${activeLanguage}`}
        value={data?.[field.key] || ''}
        label={FIELD_CONFIG[field.configKey].label}
        updateValue={(val, hasError) =>
          handleFieldChange(field.key, val, hasError)
        }
        uiSchema={FIELD_CONFIG[field.configKey]}
        trim={false}
      />
    ),
    [data, handleFieldChange, activeLanguage]
  );

  const variant = useMemo(() => {
    if (
      targetType === TARGET_TYPES.EarlyPaymentTarget ||
      targetType === TARGET_TYPES.DropSizeTarget
    ) {
      return DISPLAY_CONTENT_VARIANT.EARLY_PAYMENT;
    }
    if (SALES_TARGET_MAIN_GROUP.includes(targetType)) {
      return DISPLAY_CONTENT_VARIANT.MAIN;
    }
    return DISPLAY_CONTENT_VARIANT.LAYERED;
  }, [targetType]);

  const isActive = languages?.some((lang) => lang.code === activeLanguage);

  if (!isActive) return <div data-testid='displayContentForm' />;

  return (
    <div data-testid='displayContentForm'>
      <div className='display-content-form'>
        <TargetTypeLabel value={data?.targetType} />

        {/* Main variant */}
        {variant === DISPLAY_CONTENT_VARIANT.MAIN && (
          <>
            {VARIANT_CONFIGS[DISPLAY_CONTENT_VARIANT.MAIN].inputRows.map(
              (row) => (
                <div key={row.className} className={row.className}>
                  {row.fields.map(renderInputField)}
                </div>
              )
            )}
            {VARIANT_CONFIGS[DISPLAY_CONTENT_VARIANT.MAIN].textareaRows.map(
              (row) => (
                <div key={row.className} className={row.className}>
                  {row.fields.map(renderTextArea)}
                </div>
              )
            )}
          </>
        )}

        {/* Layered variant */}
        {variant === DISPLAY_CONTENT_VARIANT.LAYERED && (
          <>
            {VARIANT_CONFIGS[DISPLAY_CONTENT_VARIANT.LAYERED].inputRows.map(
              (row) => (
                <div key={row.className} className={row.className}>
                  {row.fields.map(renderInputField)}
                </div>
              )
            )}
            {VARIANT_CONFIGS[DISPLAY_CONTENT_VARIANT.LAYERED].textareaRows.map(
              (row) => (
                <div key={row.className} className={row.className}>
                  {row.fields.map(renderTextArea)}
                </div>
              )
            )}
          </>
        )}

        {/* EarlyPayment variant */}
        {variant === DISPLAY_CONTENT_VARIANT.EARLY_PAYMENT && (
          <>
            {VARIANT_CONFIGS[
              DISPLAY_CONTENT_VARIANT.EARLY_PAYMENT
            ].inputRows.map((row) => (
              <div
                key={row.className}
                className={row.className}
                data-testid={row.dataTestId}>
                {row.fields.map(renderInputField)}
              </div>
            ))}
            {VARIANT_CONFIGS[
              DISPLAY_CONTENT_VARIANT.EARLY_PAYMENT
            ].textareaRows.map((row) => (
              <div
                key={row.className}
                className={row.className}
                data-testid={row.dataTestId}>
                {row.fields.map(renderTextArea)}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

DisplayContentTab.propTypes = {
  setDisplayContentFormData: PropTypes.func.isRequired,
  displayContentFormData: PropTypes.object.isRequired,
  activeLanguage: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string
    })
  ).isRequired,
  targetType: PropTypes.string.isRequired
};

export default React.memo(DisplayContentTab);
